import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../../shared/components/Spinner';
import HistoricCall from '../components/HistoricCall';
import { fetchCallHistory } from '../reducers/callHistory';
import { StoreDispatch, StoreState } from '../store';
import * as styles from '../styles/callHistoryList.module.scss';
import ListSeparator from './ListSeparator';

const CallHistoryList: React.FC = () => {
  const dispatch = useDispatch<StoreDispatch>();
  const calls = useSelector((state: StoreState) => state.callHistory.calls);
  const isFetching = useSelector(
    (state: StoreState) => state.callHistory.isFetching,
  );
  const [pinnedElementId, setPinnedElementId] = useState(0);

  const callListRef = useRef<HTMLUListElement>(null);
  const dateSeparatorsRef = useRef<{ id: number; offsetTop: number }[]>([]);

  const registerSeparator = useCallback((id: number, offsetTop: number) => {
    dateSeparatorsRef.current.push({
      id,
      offsetTop,
    });
  }, []);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      // Firefox doesn't support .srcElement, it uses .target
      const el = (e.srcElement || e.target) as HTMLElement;
      const scrollPosition = el?.scrollTop;
      const dateSeparators = dateSeparatorsRef.current;

      let pinIdx = dateSeparators.findIndex((x) => {
        return scrollPosition < x.offsetTop;
      });

      // If pinIdx is -1 it means we're at the last element, so
      // set pinIdx appropriately
      pinIdx = pinIdx === -1 ? dateSeparators.length : pinIdx;

      const pinnedElement = dateSeparators[pinIdx - 1];
      if (!pinnedElement) {
        return;
      }
      const pinnedElementId = pinnedElement.id;

      setPinnedElementId(pinnedElementId);

      const totalHeight = el.scrollHeight;
      const visibleHeight = el.offsetHeight;
      const visibleBottom = scrollPosition + visibleHeight;
      const approachingEnd = totalHeight - visibleBottom <= 300;

      if (approachingEnd) {
        dispatch(fetchCallHistory({ overrideParams: {} }));
      }
    };
    const currentRef = callListRef.current;
    currentRef?.addEventListener('scroll', handleScroll);

    return () => currentRef?.removeEventListener('scroll', handleScroll);
  }, [dispatch]);

  let day: number;

  return (
    <ul className={styles.list} ref={callListRef}>
      {calls
        ? calls.map((call, i) => {
            const callMoment = DateTime.fromISO(call.callStarted);
            const callDay = callMoment.ordinal;
            let date;
            if (!day || callDay !== day) {
              day = callDay;
              date = callMoment.toFormat('dd MMMM yyyy');
            }
            return (
              <div key={call.id}>
                {date ? (
                  <ListSeparator
                    id={i}
                    pinnedElementId={pinnedElementId}
                    title={date}
                    renderCallback={registerSeparator}
                  />
                ) : (
                  ''
                )}
                <HistoricCall call={call} />
              </div>
            );
          })
        : ''}
      {isFetching ? (
        <div className={styles.spinner_container}>
          {calls.length ? <div className={styles.spinner_line}></div> : ''}
          <Spinner text='Loading' />
        </div>
      ) : (
        ''
      )}
    </ul>
  );
};

export default React.memo(
  CallHistoryList /*, (prevProps, nextProps) => {
  const change = Object.keys(prevProps).some((key) => {
    const current = prevProps[key as keyof Props];
    const next = nextProps[key as keyof Props];
    if (Array.isArray(current) && Array.isArray(next)) {
      // We can't directly compare array, so compare length instead
      return next.length !== current.length;
    } else {
      return next !== current;
    }
  });

  // If there's been a change, render
  // If not, don't
  return change;
}*/,
);
